import { Button } from "components/DesignSystem/Button/Button";
import { useParams } from "react-router-dom";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { usePageTitle } from "hooks/usePageTitle";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Table from "components/DesignSystem/Table/V2/Table";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useMemo, useState, useCallback } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  ReconciliationHistoryItem,
  useGetReconciliationHistoryQuery,
  useExportReconciliationMutation,
} from "store/apis/reconciliation";
import { DashboardLayout } from "components/DashboardLayout";
import { Header } from "components/DesignSystem/Header/Header";
import { ReconcileButton } from "../ReconcileButton";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import Wind from "components/icons/Wind";
import { useModal } from "hooks/useModal";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { Preview } from "components/PreviewModal";
import { openLink } from "utils/openLink";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { FileIcon } from "components/design/FileIcon";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { isStripeBank } from "utils/bankNameAcc";

export const ReconciliationHistory = () => {
  const { bankAccountId } = useParams<{ bankAccountId: string }>();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { link } = useConstructInternalLink();
  const { alertToast } = useToast();
  const previewModal = useModal();
  const [previewDocumentId, setPreviewDocumentId] = useState<string>();
  const [loadingReconciliationId, setLoadingReconciliationId] = useState<
    string | null
  >(null);
  const [actionType, setActionType] = useState<"view" | "export" | null>(null);

  const [exportReconciliation] = useExportReconciliationMutation();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.ACCOUNTING
  );

  const [pageNum, setPageNum] = useState(1);

  const {
    data: historyResponse,
    isLoading,
    isSuccess,
    isFetching,
  } = useGetReconciliationHistoryQuery(
    {
      entityId,
      entity_bank_account_ids: bankAccountId,
      page_num: pageNum,
    },
    { skip: !entityId || !bankAccountId }
  );

  const {
    reconciliations = [],
    per_page = 10,
    total_count = 0,
    total_pages = 1,
  } = historyResponse || {};

  const { data: ledger } = useGetEntityBanksQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId }
  );

  const currentAccount = ledger?.accounts?.find(
    (acc) => acc.account.uuid === bankAccountId
  );

  const bankName = reconciliations?.[0]?.bank_account?.name || "Bank";

  const handleView = useCallback(
    async (reconciliationId: string) => {
      setLoadingReconciliationId(reconciliationId);
      setActionType("view");
      try {
        const { document_id } = await exportReconciliation({
          entityId,
          reconciliation_ids: reconciliationId,
          export_to_mail: false,
        }).unwrap();

        if (document_id) {
          setPreviewDocumentId(document_id);
          previewModal.open();
        }
      } catch (error) {
        alertToast({
          message: (error as BackendError)?.data?.error?.message,
        });
      } finally {
        setLoadingReconciliationId(null);
        setActionType(null);
      }
    },
    [
      entityId,
      exportReconciliation,
      setPreviewDocumentId,
      previewModal,
      alertToast,
    ]
  );

  const handleExport = useCallback(
    async (reconciliationId: string) => {
      setLoadingReconciliationId(reconciliationId);
      setActionType("export");
      try {
        const { document_id } = await exportReconciliation({
          entityId,
          reconciliation_ids: reconciliationId,
          export_to_mail: false,
        }).unwrap();

        if (document_id) {
          const { download_url } = await getPreviewUrl({
            groupId: groupId!,
            fileId: document_id,
          }).unwrap();
          openLink(download_url);
        }
      } catch (error) {
        alertToast({
          message: (error as BackendError)?.data?.error?.message,
        });
      } finally {
        setLoadingReconciliationId(null);
        setActionType(null);
      }
    },
    [entityId, exportReconciliation, groupId, getPreviewUrl, alertToast]
  );

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<ReconciliationHistoryItem>();
    return [
      columnHelper.accessor(
        (row) =>
          `${row.reconcilation_start_date} - ${row.reconcilation_end_date}`,
        {
          id: "Reconciliation period",
          size: 200,
          cell: (info) => {
            const { reconcilation_start_date, reconcilation_end_date } =
              info.row.original;
            return (
              <div className="t-text-text-60 t-text-body-sm">
                {dayjs(reconcilation_start_date).format(DISPLAY_DATE)} -{" "}
                {dayjs(reconcilation_end_date).format(DISPLAY_DATE)}
              </div>
            );
          },
          header: () => <HeadCellContent title="Reconciliation period" />,
        }
      ),
      columnHelper.accessor("reconciled_by", {
        id: "Reconciled by",
        cell: (info) => (
          <div className="t-text-text-60 t-text-body-sm">{info.getValue()}</div>
        ),
        header: () => <HeadCellContent title="Reconciled by" />,
      }),
      columnHelper.accessor("last_reconciled_date", {
        id: "Reconciled on",
        cell: (info) => (
          <div className="t-text-text-60 t-text-body-sm">
            {dayjs(info.getValue()).format(DISPLAY_DATE)}
          </div>
        ),
        header: () => <HeadCellContent title="Reconciled on" />,
      }),
      columnHelper.accessor("customer_statement_ending_balance", {
        id: "Ending Balance",
        cell: (info) => (
          <div className="t-text-text-60 t-text-body-sm t-flex t-justify-end">
            $
            {info.getValue().toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        ),
        header: () => <HeadCellContent title="Ending Balance" align="right" />,
      }),
      columnHelper.accessor("bank_csv_id", {
        id: "Account Statement",
        cell: (info) => {
          const csvId = info.getValue();
          const hasCsv = !!csvId;

          const handleCsvClick = async () => {
            if (!csvId) return;

            setPreviewDocumentId(csvId);
            previewModal.open();
          };

          return (
            <div className="t-flex t-justify-center">
              {hasCsv ? (
                <Button
                  type="button"
                  customType="ghost_icon"
                  size="small"
                  onClick={handleCsvClick}
                >
                  <FileIcon fileType="CSV" size="20" />
                </Button>
              ) : (
                <span className="t-text-text-60 t-text-body-sm">-</span>
              )}
            </div>
          );
        },
        header: () => (
          <HeadCellContent title="Account Statement" align="center" />
        ),
      }),
      columnHelper.accessor((row) => row, {
        id: "Actions",
        cell: (info) => {
          const reconciliationId = info.row.original.reconcilation_id;
          const isLoading = loadingReconciliationId === reconciliationId;
          const isViewLoading = isLoading && actionType === "view";
          const isExportLoading = isLoading && actionType === "export";

          return (
            <div className="t-flex t-gap-2">
              <Button
                size="small"
                customType="secondary"
                onClick={() => handleView(reconciliationId)}
                isLoading={isViewLoading}
                disabled={isViewLoading}
              >
                View
              </Button>
              <Button
                size="small"
                onClick={() => handleExport(reconciliationId)}
                isLoading={isExportLoading}
                disabled={isExportLoading}
              >
                Export
              </Button>
            </div>
          );
        },
        header: () => null,
      }),
    ];
  }, [
    handleView,
    handleExport,
    loadingReconciliationId,
    actionType,
    previewModal,
  ]);

  const table = useReactTable({
    data: reconciliations,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const isEmpty = !reconciliations || reconciliations.length === 0;

  return (
    <DashboardLayout
      header={
        <Header
          v2
          title={<span>{bankName}</span>}
          breadcrumbs={[
            {
              name: "Reconciliation",
              link: link(`/books/reconciliation`),
            },
          ]}
          right={
            currentAccount && (
              <ReconcileButton
                bankAccountId={bankAccountId}
                entityId={entityId}
                lastReconciledDate={currentAccount.account.last_reconciled_date}
                reconciliationStartDate={
                  currentAccount.account.reconcilation_start_date
                }
                parentPage="history"
                customType="primary"
                size="regular"
                hasWritePermission={hasWritePermission}
                isStripe={isStripeBank(currentAccount)}
              />
            )
          }
        />
      }
    >
      <Async.Root {...{ isLoading, isEmpty, isSuccess }}>
        <Async.Empty>
          <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-6 t-py-20 t-text-neutral-40">
            <Wind />
            <p className="t-text-body-sm t-m-0">
              No reconciliations done so far
            </p>
            {currentAccount && (
              <ReconcileButton
                bankAccountId={bankAccountId}
                entityId={entityId}
                lastReconciledDate={currentAccount.account.last_reconciled_date}
                reconciliationStartDate={
                  currentAccount.account.reconcilation_start_date
                }
                parentPage="history"
                customType="primary_outlined"
                size="regular"
                hasWritePermission={hasWritePermission}
                isStripe={isStripeBank(currentAccount)}
              />
            )}
          </div>
        </Async.Empty>
        <Async.Success>
          <div className="t-flex t-flex-col t-gap-2 t-pb-20">
            <div className="t-flex t-justify-end t-sticky t-top-0 t-z-10 t-bg-white">
              <PaginationHandler
                page_num={pageNum}
                per_page={per_page}
                total_count={total_count}
                total_pages={total_pages}
                onPageNumChange={setPageNum}
                isLoading={isFetching}
              />
            </div>

            <Table.Container>
              <Table.Content>
                <Table.Head>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Table.Row key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <Table.HeadCell
                          key={header.id}
                          style={{ width: header.getSize() }}
                          isUpperCase={false}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </Table.HeadCell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Head>
                <Table.Body>
                  {table.getRowModel().rows.map((row) => (
                    <Table.Row key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell
                          key={cell.id}
                          style={{ width: cell.column.getSize() }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.Container>
          </div>
        </Async.Success>
      </Async.Root>
      {previewModal.isOpen && previewDocumentId && (
        <Preview
          showModal={previewModal.isOpen}
          closeModal={previewModal.close}
          groupId={groupId!}
          previewId={previewDocumentId}
        />
      )}
    </DashboardLayout>
  );
};
